<script setup>
import { ref, computed } from 'vue';
import Header from './components/Header.vue';
import Footer from './components/Footer.vue';

// Reactive states
const searchQuery = ref('');
const selectedArticleId = ref(null);
const feedbackGiven = ref(false);

const submitFeedback = () => {
  feedbackGiven.value = true;
};

// Help Center Articles Database
const articles = [
  {
    id: 'quick-start',
    category: 'Getting Started',
    title: 'Quick Start Guide',
    icon: 'play_circle',
    description: 'Get up and running with OS DESK in 5 minutes.',
    content: `
      <div class="space-y-6">
        <p class="text-base text-slate-600 leading-relaxed">
          Welcome to the <strong>OS DESK Help Center</strong>. This guide is designed to get your customer support workspace configured, integrated, and ready to go in less than 5 minutes.
        </p>

        <div class="bg-teal-50 border-l-4 border-teal-600 p-4 rounded-r-xl">
          <h4 class="font-bold text-teal-800 mb-1 text-sm">Prerequisites</h4>
          <p class="text-xs text-slate-600">You must have an Administrator account or a workspace invitation link from your company administrator.</p>
        </div>

        <h3 class="text-xl font-bold text-slate-900 mt-6">1. Log in to the Application Portal</h3>
        <p class="text-base text-slate-600 leading-relaxed">
          Head to the platform log in page at <a href="/app/login" class="text-[#006a61] font-semibold hover:underline">/app/login</a>. You have three secure ways to authenticate:
        </p>
        <ul class="list-disc list-inside pl-4 text-slate-600 space-y-2">
          <li><strong>Credentials</strong>: Use your corporate email and password. Click the eye icon to view your password if needed.</li>
          <li><strong>Google Login</strong>: Click the Google button to authenticate instantly via Google Workspace.</li>
          <li><strong>SAML SSO</strong>: Enter your organization domain to log in via your Identity Provider (e.g. Okta, Azure AD).</li>
        </ul>

        <h3 class="text-xl font-bold text-slate-900 mt-6">2. Complete Your Account Setup</h3>
        <p class="text-base text-slate-600 leading-relaxed">
          Upon logging in, check your sidebar settings:
        </p>
        <ul class="list-disc list-inside pl-4 text-slate-600 space-y-2">
          <li><strong>Configure Profile</strong>: Click your profile icon at the bottom of the sidebar to edit your name, active hours, and email notification preferences.</li>
          <li><strong>Invite Your Team</strong>: Head over to <em>Settings > Agents</em> to send email invites to other support agents and supervisors.</li>
        </ul>

        <h3 class="text-xl font-bold text-slate-900 mt-6">3. Handle Conversations</h3>
        <p class="text-base text-slate-600 leading-relaxed">
          Incoming customer tickets are routed to the <strong>Conversations</strong> tab. From there, you can view the queue, assign conversations to specific agents or teams, and respond in real-time.
        </p>
      </div>
    `
  },
  {
    id: 'integrating-channels',
    category: 'Getting Started',
    title: 'Integrating Channels',
    icon: 'hub',
    description: 'Connect email, WhatsApp, and live chat widgets.',
    content: `
      <div class="space-y-6">
        <p class="text-base text-slate-600 leading-relaxed">
          OS DESK acts as a single pane of glass, letting you respond to customers on Email, WhatsApp, Web, and SMS simultaneously.
        </p>

        <h3 class="text-xl font-bold text-slate-900 mt-6">Email Support Channel</h3>
        <p class="text-base text-slate-600 leading-relaxed">
          To receive and reply to support inquiries on your custom company domain (such as <code class="bg-slate-100 px-1.5 py-0.5 rounded font-mono text-sm">support@onlinesavaari.com</code>):
        </p>
        <ul class="list-decimal list-inside pl-4 text-slate-600 space-y-2">
          <li>Navigate to <strong>Settings > Inboxes</strong> and click <strong>Add Inbox</strong>.</li>
          <li>Select <strong>Email</strong> as your channel.</li>
          <li>Input your email address and generate a unique forwarding mail link.</li>
          <li>Set up email forwarding in your email service provider (e.g. Gmail or Outlook) to forward copy of messages to that link.</li>
        </ul>

        <h3 class="text-xl font-bold text-slate-900 mt-6">WhatsApp Integration</h3>
        <p class="text-base text-slate-600 leading-relaxed">
          Link your company's WhatsApp business line (e.g., <strong>7003601355</strong>) to communicate directly with mobile customers:
        </p>
        <ul class="list-decimal list-inside pl-4 text-slate-600 space-y-2">
          <li>Select the <strong>WhatsApp</strong> channel on the Inboxes page.</li>
          <li>Choose your Meta Business integration credentials.</li>
          <li>Verify your phone number via a standard SMS code.</li>
          <li>Configure an auto-responder template for fast off-hours greetings.</li>
        </ul>

        <h3 class="text-xl font-bold text-slate-900 mt-6">Website Live Chat Widget</h3>
        <p class="text-base text-slate-600 leading-relaxed">
          Interact with website visitors using our custom web widget:
        </p>
        <ul class="list-decimal list-inside pl-4 text-slate-600 space-y-2">
          <li>Create a <strong>Website</strong> channel in settings.</li>
          <li>Copy the generated custom HTML/JS code snippet.</li>
          <li>Embed the snippet inside your web app layout file before the closing <code class="bg-slate-100 px-1.5 py-0.5 rounded font-mono text-sm">&lt;/body&gt;</code> tag.</li>
        </ul>
      </div>
    `
  },
  {
    id: 'team-permissions',
    category: 'Getting Started',
    title: 'Team Permissions & Roles',
    icon: 'group',
    description: 'Manage users, teams, and access control.',
    content: `
      <div class="space-y-6">
        <p class="text-base text-slate-600 leading-relaxed">
          Control access scopes to sensitive folders, billing info, and customer records by defining team permissions.
        </p>

        <h3 class="text-xl font-bold text-slate-900 mt-6">Understanding User Roles</h3>
        <div class="overflow-x-auto">
          <table class="w-full text-left border-collapse border border-slate-200 mt-2">
            <thead>
              <tr class="bg-slate-50">
                <th class="p-3 border border-slate-200 font-semibold text-slate-700 text-sm">Role Name</th>
                <th class="p-3 border border-slate-200 font-semibold text-slate-700 text-sm">Permissions</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="p-3 border border-slate-200 font-medium text-slate-800 text-sm">Administrator</td>
                <td class="p-3 border border-slate-200 text-slate-600 text-sm">Full read/write permissions for all Inboxes, Teams, Integrations, and Billing settings. Can add/remove agents.</td>
              </tr>
              <tr class="bg-slate-50/50">
                <td class="p-3 border border-slate-200 font-medium text-slate-800 text-sm">Supervisor</td>
                <td class="p-3 border border-slate-200 text-slate-600 text-sm">Can view/reply to assigned tickets, assign conversations, access reports, and manage canned responses.</td>
              </tr>
              <tr>
                <td class="p-3 border border-slate-200 font-medium text-slate-800 text-sm">Agent</td>
                <td class="p-3 border border-slate-200 text-slate-600 text-sm">Read/reply access to conversations belonging to their assigned inboxes and teams.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 class="text-xl font-bold text-slate-900 mt-6">Creating Departmental Teams</h3>
        <p class="text-base text-slate-600 leading-relaxed">
          Group agents into distinct teams (e.g. Sales, Technical Support, Billing):
        </p>
        <ul class="list-decimal list-inside pl-4 text-slate-600 space-y-2">
          <li>Navigate to <strong>Settings > Teams</strong> and click <strong>Create Team</strong>.</li>
          <li>Name the team and assign member agents.</li>
          <li>When creating or editing Inboxes, route conversation assignments directly to the team.</li>
        </ul>
      </div>
    `
  },
  {
    id: 'basic-config',
    category: 'Getting Started',
    title: 'Basic Config',
    icon: 'settings',
    description: 'Configure basic workspace details and domain names.',
    content: `
      <div class="space-y-6">
        <p class="text-base text-slate-600 leading-relaxed">
          Get your workspace ready for production by fine-tuning corporate configuration variables.
        </p>

        <h3 class="text-xl font-bold text-slate-900 mt-6">Subdomain Configuration</h3>
        <p class="text-base text-slate-600 leading-relaxed">
          Set up custom subdomain settings under <strong>Settings > Account</strong> to whitelabel your portal URL:
        </p>
        <ul class="list-disc list-inside pl-4 text-slate-600 space-y-2">
          <li>Enter your custom company name.</li>
          <li>Define a custom support portal domain (e.g. <code class="bg-slate-100 px-1.5 py-0.5 rounded font-mono text-sm">support.onlinesavaari.com</code>) to allow agents to login easily.</li>
        </ul>

        <h3 class="text-xl font-bold text-slate-900 mt-6">Business Hours & SLAs</h3>
        <p class="text-base text-slate-600 leading-relaxed">
          Define when your support agents are active:
        </p>
        <ul class="list-disc list-inside pl-4 text-slate-600 space-y-2">
          <li>Set global timezone and working days/hours.</li>
          <li>Establish Service Level Agreements (SLAs) with custom warning thresholds and automatic escalation paths when tickets remain unassigned for too long.</li>
        </ul>
      </div>
    `
  },
  {
    id: 'prompt-opt',
    category: 'AI Agents',
    title: 'Prompt Optimization',
    icon: 'tune',
    description: 'Tune instructions and personality for AEROX COPILOT.',
    content: `
      <div class="space-y-6">
        <p class="text-base text-slate-600 leading-relaxed">
          <strong>AEROX COPILOT</strong> is our premium AI agent system designed to automate customer support routing, drafting, and ticket summarization.
        </p>

        <div class="bg-emerald-50 border-l-4 border-emerald-600 p-4 rounded-r-xl">
          <h4 class="font-bold text-emerald-800 mb-1 text-sm">Plan Availability Notice</h4>
          <p class="text-xs text-slate-600">AEROX COPILOT features are fully available to all accounts on the <strong>Professional</strong> and <strong>Enterprise</strong> plans.</p>
        </div>

        <h3 class="text-xl font-bold text-slate-900 mt-6">Writing Custom Agent Instructions</h3>
        <p class="text-base text-slate-600 leading-relaxed">
          You can program the AI copilot to write in a specific corporate persona and adhere to exact guardrails:
        </p>
        <ul class="list-decimal list-inside pl-4 text-slate-600 space-y-2">
          <li>Go to <strong>Settings > AEROX Copilot</strong>.</li>
          <li>In the <strong>System Instructions</strong> text area, define behavior rules. Example:
            <pre class="bg-slate-900 text-slate-300 p-3 rounded-lg font-mono text-xs overflow-x-auto mt-2">"You are a helpful customer support agent for Online Savaari. Speak politely, keep replies under 150 words, and never give discount percentages without admin authorization."</pre>
          </li>
          <li>Click <strong>Save Instructions</strong> to apply the rules.</li>
        </ul>
      </div>
    `
  },
  {
    id: 'knowledge-retrieval',
    category: 'AI Agents',
    title: 'Knowledge Retrieval',
    icon: 'database',
    description: 'Train AEROX COPILOT on documents and website URLs.',
    content: `
      <div class="space-y-6">
        <p class="text-base text-slate-600 leading-relaxed">
          Enable Retrieval-Augmented Generation (RAG) for AEROX COPILOT so it has direct access to your internal databases and knowledge base articles.
        </p>

        <h3 class="text-xl font-bold text-slate-900 mt-6">Adding Context Documents</h3>
        <p class="text-base text-slate-600 leading-relaxed">
          Upload PDF guides, policy lists, or past ticket history directly to train the AI:
        </p>
        <ul class="list-decimal list-inside pl-4 text-slate-600 space-y-2">
          <li>Navigate to <strong>Settings > AEROX Copilot > Knowledge Base</strong>.</li>
          <li>Upload document files or input public documentation URLs to crawl.</li>
          <li>The platform automatically chunks, processes, and stores vector embeddings.</li>
        </ul>

        <h3 class="text-xl font-bold text-slate-900 mt-6">Response Generation Flow</h3>
        <p class="text-base text-slate-600 leading-relaxed">
          When a customer sends a message:
        </p>
        <ul class="list-disc list-inside pl-4 text-slate-600 space-y-2">
          <li>AEROX COPILOT performs semantic lookup over the Vector database.</li>
          <li>Relevant excerpts are pulled as grounding context.</li>
          <li>The AI formats a draft response referencing the facts in your docs, avoiding hallucination.</li>
        </ul>
      </div>
    `
  },
  {
    id: 'manage-account',
    category: 'Billing & Account',
    title: 'Manage Account',
    icon: 'payments',
    description: 'Upgrade subscriptions, seats, and billing cycles.',
    content: `
      <div class="space-y-6">
        <p class="text-base text-slate-600 leading-relaxed">
          Manage subscriptions, payment profiles, and purchase extra agent seats.
        </p>

        <h3 class="text-xl font-bold text-slate-900 mt-6">Plan Comparison</h3>
        <div class="overflow-x-auto">
          <table class="w-full text-left border-collapse border border-slate-200 mt-2">
            <thead>
              <tr class="bg-slate-50">
                <th class="p-3 border border-slate-200 font-semibold text-slate-700 text-sm">Feature</th>
                <th class="p-3 border border-slate-200 font-semibold text-slate-700 text-sm">Starter</th>
                <th class="p-3 border border-slate-200 font-semibold text-slate-700 text-sm">Professional</th>
                <th class="p-3 border border-slate-200 font-semibold text-slate-700 text-sm">Enterprise</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="p-3 border border-slate-200 font-medium text-slate-800 text-sm">AEROX COPILOT</td>
                <td class="p-3 border border-slate-200 text-rose-500 text-sm">No</td>
                <td class="p-3 border border-slate-200 text-emerald-600 font-semibold text-sm">Yes</td>
                <td class="p-3 border border-slate-200 text-emerald-600 font-semibold text-sm">Yes</td>
              </tr>
              <tr class="bg-slate-50/50">
                <td class="p-3 border border-slate-200 font-medium text-slate-800 text-sm">Custom Domain</td>
                <td class="p-3 border border-slate-200 text-rose-500 text-sm">No</td>
                <td class="p-3 border border-slate-200 text-emerald-600 font-semibold text-sm">Yes</td>
                <td class="p-3 border border-slate-200 text-emerald-600 font-semibold text-sm">Yes</td>
              </tr>
              <tr>
                <td class="p-3 border border-slate-200 font-medium text-slate-800 text-sm">Custom SLAs</td>
                <td class="p-3 border border-slate-200 text-rose-500 text-sm">No</td>
                <td class="p-3 border border-slate-200 text-rose-500 text-sm">No</td>
                <td class="p-3 border border-slate-200 text-emerald-600 font-semibold text-sm">Yes</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 class="text-xl font-bold text-slate-900 mt-6">Upgrading Subscription</h3>
        <ul class="list-disc list-inside pl-4 text-slate-600 space-y-2">
          <li>Navigate to <strong>Settings > Billing</strong>.</li>
          <li>Choose a plan tier and submit payment details to unlock features instantly.</li>
        </ul>
      </div>
    `
  },
  {
    id: 'trust-center',
    category: 'Security',
    title: 'Trust Center',
    icon: 'shield_lock',
    description: 'Setup two-factor authentication, SAML/SSO, and compliance.',
    content: `
      <div class="space-y-6">
        <p class="text-base text-slate-600 leading-relaxed">
          At OS DESK, we run enterprise-class security frameworks to ensure user data protection and privacy compliance.
        </p>

        <h3 class="text-xl font-bold text-slate-900 mt-6">Enforcing Two-Factor Authentication (2FA)</h3>
        <p class="text-base text-slate-600 leading-relaxed">
          Mandate 2FA for all members of your team:
        </p>
        <ul class="list-decimal list-inside pl-4 text-slate-600 space-y-2">
          <li>Administrators can navigate to <strong>Settings > Security</strong>.</li>
          <li>Toggle <strong>Force 2FA</strong>.</li>
          <li>All agents will be forced to scan an authenticator barcode to log in at their next session.</li>
        </ul>

        <h3 class="text-xl font-bold text-slate-900 mt-6">SAML/SSO</h3>
        <p class="text-base text-slate-600 leading-relaxed">
          Configure single sign-on links through Azure AD, Okta, or Google Workspace to manage corporate access securely.
        </p>
      </div>
    `
  },
  {
    id: 'api-docs',
    category: 'Developers',
    title: 'API Docs',
    icon: 'code',
    description: 'REST API endpoints, webhooks, and request/response reference.',
    content: `
      <div class="space-y-6">
        <p class="text-base text-slate-600 leading-relaxed">
          Automate data retrieval and ticket updates using our REST API. Authenticate all requests by passing your API token in the authorization header:
        </p>

        <div class="bg-slate-900 text-slate-100 p-4 rounded-lg font-mono text-sm border border-slate-800">
          Authorization: Bearer &lt;YOUR_API_TOKEN&gt;
        </div>

        <h3 class="text-xl font-bold text-slate-900 mt-6">1. Submit a Contact Inquiry</h3>
        <p class="text-base text-slate-600 leading-relaxed">
          Use this endpoint to post user contact requests from external websites to your workspace:
        </p>
        <div class="bg-slate-900 text-slate-200 p-4 rounded-lg font-mono text-xs overflow-x-auto space-y-2 mb-2">
          <span class="text-emerald-400">POST</span> /public/api/v1/contact_inquiries
        </div>
        <p class="text-sm font-semibold text-slate-700">Request Body JSON Schema:</p>
        <pre class="bg-slate-900 text-slate-300 p-4 rounded-lg font-mono text-xs overflow-x-auto mt-1">{
  "name": "John Doe",
  "email": "john@onlinesavaari.com",
  "companySize": "11-50",
  "topic": "technical",
  "message": "We need help setting up the webhook integration."
}</pre>
        <p class="text-sm font-semibold text-slate-700 mt-2">Response:</p>
        <div class="bg-slate-900 text-slate-300 p-4 rounded-lg font-mono text-xs overflow-x-auto mt-1">{
  "status": "success",
  "message": "Inquiry recorded successfully."
}</div>

        <h3 class="text-xl font-bold text-slate-900 mt-6">2. Retrieve Conversations</h3>
        <p class="text-base text-slate-600 leading-relaxed">
          List conversations inside an account:
        </p>
        <div class="bg-slate-900 text-slate-200 p-4 rounded-lg font-mono text-xs overflow-x-auto mb-2">
          <span class="text-emerald-400">GET</span> /api/v1/accounts/{account_id}/conversations
        </div>

        <h3 class="text-xl font-bold text-slate-900 mt-6">3. Send a Reply</h3>
        <p class="text-base text-slate-600 leading-relaxed">
          Post an outgoing message to a conversation thread:
        </p>
        <div class="bg-slate-900 text-slate-200 p-4 rounded-lg font-mono text-xs overflow-x-auto space-y-2 mb-2">
          <span class="text-emerald-400">POST</span> /api/v1/accounts/{account_id}/conversations/{conversation_id}/messages
        </div>
        <pre class="bg-slate-900 text-slate-300 p-4 rounded-lg font-mono text-xs overflow-x-auto mt-1">{
  "content": "Hello, thank you for contacting us! An agent will review your inquiry.",
  "message_type": "outgoing"
}</pre>
      </div>
    `
  }
];

// Computed search results
const filteredArticles = computed(() => {
  if (!searchQuery.value.trim()) return [];
  const query = searchQuery.value.toLowerCase().trim();
  return articles.filter(article => 
    article.title.toLowerCase().includes(query) ||
    article.description.toLowerCase().includes(query) ||
    article.content.toLowerCase().includes(query) ||
    article.category.toLowerCase().includes(query)
  );
});

// Computed selected article details
const selectedArticle = computed(() => {
  return articles.find(a => a.id === selectedArticleId.value);
});

const filteredSidebarArticles = computed(() => {
  if (!selectedArticle.value) return [];
  return articles.filter(a => a.category === selectedArticle.value.category);
});
</script>

<template>
  <div class="scroll-smooth">
    <!-- TopNavBar (Shared Component) -->
    <Header />

    <!-- Hero Search Section -->
    <section
      v-if="!selectedArticle"
      class="relative pt-stack-xl pb-24 overflow-hidden bg-surface-container-low"
    >
      <div
        class="absolute top-0 right-0 w-1/3 h-full opacity-10 pointer-events-none"
      />
      <div
        class="max-w-container-max mx-auto px-margin-desktop relative z-10 text-center"
      >
        <span
          class="inline-block px-stack-md py-1 bg-secondary-container text-on-secondary-container rounded-full font-label-md text-caption mb-stack-md"
          >HELP CENTER</span>
        <h1
          class="font-headline-lg text-headline-lg md:text-display-xl md:font-display-xl text-deep-navy mb-stack-md max-w-3xl mx-auto"
        >
          How can we help you today?
        </h1>
        <!-- AI Search Bar -->
        <div class="max-w-2xl mx-auto mt-stack-lg">
          <div
            class="ai-glow-border bg-surface-container-lowest rounded-xl shadow-lg flex items-center p-2 focus-within:ring-2 ring-primary/10 transition-all"
          >
            <span class="material-symbols-outlined ml-stack-md text-outline">search</span>
            <input
              v-model="searchQuery"
              class="w-full border-none focus:ring-0 bg-transparent text-body-lg font-body-lg px-stack-md py-stack-sm text-on-surface"
              placeholder="Search for documentation, tutorials, or ask AI..."
              type="text"
            />
            <button
              v-if="searchQuery"
              @click="searchQuery = ''"
              class="mr-2 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
            >
              <span class="material-symbols-outlined text-[20px]">close</span>
            </button>
            <button
              class="bg-primary text-on-primary px-stack-lg py-stack-sm rounded-lg font-label-md flex items-center gap-2 group transition-all"
            >
              <span>Search</span>
              <span
                class="material-symbols-outlined text-[20px] group-hover:translate-x-1 transition-transform"
                >arrow_forward</span>
            </button>
          </div>
          <!-- Quick AI Suggestions -->
          <div
            class="mt-stack-md flex flex-wrap justify-center gap-stack-sm items-center"
          >
            <span class="font-caption text-caption text-outline">Quick AI Answers:</span>
            <button
              @click="selectedArticleId = 'prompt-opt'; searchQuery = ''; feedbackGiven = false;"
              class="bg-ai-glow border border-secondary-fixed-dim/30 px-3 py-1 rounded-full text-caption font-caption text-on-secondary-container hover:bg-secondary-container transition-colors cursor-pointer"
            >
              How to setup AI Agent?
            </button>
            <button
              @click="selectedArticleId = 'api-docs'; searchQuery = ''; feedbackGiven = false;"
              class="bg-ai-glow border border-secondary-fixed-dim/30 px-3 py-1 rounded-full text-caption font-caption text-on-secondary-container hover:bg-secondary-container transition-colors cursor-pointer"
            >
              Reset API keys
            </button>
            <button
              @click="selectedArticleId = 'manage-account'; searchQuery = ''; feedbackGiven = false;"
              class="bg-ai-glow border border-secondary-fixed-dim/30 px-3 py-1 rounded-full text-caption font-caption text-on-secondary-container hover:bg-secondary-container transition-colors cursor-pointer"
            >
              Billing cycles
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Search Results / Article View / Bento Grid -->
    <section class="py-stack-xl max-w-container-max mx-auto px-margin-desktop">
      
      <!-- 1. Search Results View -->
      <div v-if="searchQuery.trim()" class="space-y-8">
        <div class="flex items-center justify-between border-b border-outline-variant pb-4">
          <h2 class="font-headline-lg text-headline-lg text-deep-navy">
            Search Results for "{{ searchQuery }}"
          </h2>
          <button 
            @click="searchQuery = ''"
            class="text-[#006a61] font-bold text-sm hover:underline flex items-center gap-1 cursor-pointer"
          >
            Clear Search
          </button>
        </div>

        <div v-if="filteredArticles.length > 0" class="grid grid-cols-1 md:grid-cols-2 gap-gutter">
          <div 
            v-for="item in filteredArticles" 
            :key="item.id"
            @click="selectedArticleId = item.id; searchQuery = ''; feedbackGiven = false;"
            class="glass-card p-stack-lg rounded-xl hover:border-primary hover:shadow-md transition-all cursor-pointer flex items-start gap-4"
          >
            <span class="material-symbols-outlined text-primary text-[28px] mt-1">{{ item.icon || 'description' }}</span>
            <div>
              <span class="text-xs text-outline uppercase tracking-wider font-semibold">{{ item.category }}</span>
              <h3 class="font-headline-md text-headline-md text-deep-navy mt-1">{{ item.title }}</h3>
              <p class="text-on-surface-variant font-body-md mt-1">{{ item.description }}</p>
            </div>
          </div>
        </div>

        <div v-else class="text-center py-16 bg-surface-container rounded-2xl border border-outline-variant">
          <span class="material-symbols-outlined text-outline text-[48px] mb-2">find_in_page</span>
          <h3 class="font-headline-md text-deep-navy">No articles found</h3>
          <p class="text-on-surface-variant font-body-md mt-1">Try searching for other terms like "AI Agent", "API Keys", or "Billing".</p>
        </div>
      </div>

      <!-- 2. Selected Article Reader View -->
      <div v-else-if="selectedArticle" class="grid grid-cols-1 lg:grid-cols-12 gap-gutter items-start">
        <!-- Sidebar Navigation -->
        <aside class="lg:col-span-3 space-y-6 bg-surface-container-low p-6 rounded-2xl border border-outline-variant">
          <button 
            @click="selectedArticleId = null" 
            class="flex items-center gap-2 text-primary font-label-md hover:underline mb-4 text-left w-full cursor-pointer"
          >
            <span class="material-symbols-outlined text-[18px]">arrow_back</span>
            Back to Help Center
          </button>
          
          <div class="space-y-4">
            <h4 class="font-label-md text-outline uppercase tracking-wider text-xs font-semibold">Articles in {{ selectedArticle.category }}</h4>
            <ul class="space-y-2">
              <li v-for="item in filteredSidebarArticles" :key="item.id">
                <button 
                  @click="selectedArticleId = item.id; feedbackGiven = false;"
                  class="w-full text-left px-3 py-2 rounded-lg text-sm transition-all cursor-pointer"
                  :class="item.id === selectedArticleId ? 'bg-primary text-on-primary font-semibold shadow-sm' : 'text-on-surface-variant hover:bg-surface-container hover:text-on-surface'"
                >
                  {{ item.title }}
                </button>
              </li>
            </ul>
          </div>
        </aside>

        <!-- Article Reader View -->
        <article class="lg:col-span-9 bg-surface-container-lowest p-8 md:p-12 rounded-3xl border border-outline-variant">
          <nav class="flex items-center gap-2 text-xs text-outline mb-6">
            <span>Help Center</span>
            <span class="material-symbols-outlined text-[12px]">chevron_right</span>
            <span>{{ selectedArticle.category }}</span>
            <span class="material-symbols-outlined text-[12px]">chevron_right</span>
            <span class="text-on-surface font-semibold">{{ selectedArticle.title }}</span>
          </nav>

          <h1 class="text-3xl md:text-4xl font-extrabold text-slate-900 mb-6 font-display tracking-tight">
            {{ selectedArticle.title }}
          </h1>

          <div v-html="selectedArticle.content" class="prose max-w-none"></div>

          <!-- Feedback Widget -->
          <div class="border-t border-outline-variant mt-12 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <span class="text-sm font-label-md text-on-surface-variant">Was this article helpful?</span>
            <div v-if="!feedbackGiven" class="flex gap-2">
              <button 
                @click="submitFeedback"
                class="inline-flex items-center gap-2 border border-outline-variant px-4 py-2 rounded-lg text-sm text-on-surface hover:bg-surface-container hover:border-primary transition-all font-semibold cursor-pointer"
              >
                <span class="material-symbols-outlined text-[18px] text-emerald-600">thumb_up</span>
                Yes, thank you
              </button>
              <button 
                @click="submitFeedback"
                class="inline-flex items-center gap-2 border border-outline-variant px-4 py-2 rounded-lg text-sm text-on-surface hover:bg-surface-container hover:border-primary transition-all font-semibold cursor-pointer"
              >
                <span class="material-symbols-outlined text-[18px] text-rose-600">thumb_down</span>
                Not really
              </button>
            </div>
            <p v-else class="text-sm font-semibold text-[#006a61] flex items-center gap-2">
              <span class="material-symbols-outlined text-[18px]">check_circle</span>
              Thank you for your feedback! We will use it to improve our docs.
            </p>
          </div>
        </article>
      </div>

      <!-- 3. Default Bento Grid Category Browser -->
      <div v-else>
        <div class="flex items-end justify-between mb-stack-lg">
          <div>
            <h2 class="font-headline-lg text-headline-lg text-deep-navy">
              Browse by Category
            </h2>
            <p class="text-on-surface-variant font-body-md mt-2">
              Find curated guides for every stage of your journey.
            </p>
          </div>
          <button
            @click="selectedArticleId = 'quick-start'"
            class="hidden md:flex items-center gap-2 text-primary font-label-md hover:underline cursor-pointer"
          >
            View all documentation
            <span class="material-symbols-outlined">open_in_new</span>
          </button>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-12 gap-gutter">
          <!-- Large Feature Card: Getting Started -->
          <div
            class="md:col-span-8 glass-card p-stack-lg rounded-xl flex flex-col md:flex-row gap-stack-lg"
          >
            <div
              class="w-full md:w-1/3 aspect-square rounded-lg overflow-hidden shrink-0"
            >
              <img
                class="w-full h-full object-cover"
                data-alt="A clean, high-tech workspace with a futuristic laptop displaying a welcome interface. The lighting is soft and bright, using a light-mode aesthetic with OS DESK's signature deep navy and mint green accents. Minimalist architectural elements create a sense of order and institutional trust for a professional help center environment."
                src="/brand-assets/portfolio/stitch_43b18ef68ac8.jpg"
              />
            </div>
            <div class="flex flex-col justify-center">
              <div class="flex items-center gap-3 mb-stack-sm">
                <span
                  class="material-symbols-outlined text-secondary"
                  style="font-variation-settings: 'FILL' 1"
                  >rocket_launch</span>
                <h3 class="font-headline-md text-headline-md text-deep-navy">
                  Getting Started
                </h3>
              </div>
              <p class="text-on-surface-variant font-body-md mb-stack-md">
                From zero to hero in 5 minutes. Learn how to configure your first
                AI Agent, connect your data sources, and invite your team.
              </p>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-stack-lg gap-y-2">
                <button
                  @click="selectedArticleId = 'quick-start'; feedbackGiven = false;"
                  class="flex items-center gap-2 text-primary hover:text-secondary font-label-md transition-colors text-left cursor-pointer"
                >
                  <span class="material-symbols-outlined text-[18px]">play_circle</span>
                  Quick Start Guide
                </button>
                <button
                  @click="selectedArticleId = 'integrating-channels'; feedbackGiven = false;"
                  class="flex items-center gap-2 text-primary hover:text-secondary font-label-md transition-colors text-left cursor-pointer"
                >
                  <span class="material-symbols-outlined text-[18px]">hub</span>
                  Integrating Channels
                </button>
                <button
                  @click="selectedArticleId = 'team-permissions'; feedbackGiven = false;"
                  class="flex items-center gap-2 text-primary hover:text-secondary font-label-md transition-colors text-left cursor-pointer"
                >
                  <span class="material-symbols-outlined text-[18px]">group</span>
                  Team Permissions
                </button>
                <button
                  @click="selectedArticleId = 'basic-config'; feedbackGiven = false;"
                  class="flex items-center gap-2 text-primary hover:text-secondary font-label-md transition-colors text-left cursor-pointer"
                >
                  <span class="material-symbols-outlined text-[18px]">settings</span>
                  Basic Config
                </button>
              </div>
            </div>
          </div>
          
          <!-- Secondary Card: AI Agents -->
          <div
            class="md:col-span-4 glass-card p-stack-lg rounded-xl flex flex-col justify-between"
          >
            <div>
              <div
                class="bg-ai-glow w-12 h-12 rounded-lg flex items-center justify-center mb-stack-md"
              >
                <span
                  class="material-symbols-outlined text-secondary"
                  style="font-variation-settings: 'FILL' 1"
                  >neurology</span>
              </div>
              <h3 class="font-headline-md text-headline-md text-deep-navy mb-2">
                AI Agents
              </h3>
              <p class="text-on-surface-variant font-body-md">
                Advanced training, prompt engineering, and performance tuning for
                your autonomous agents.
              </p>
            </div>
            <ul class="mt-stack-md space-y-3">
              <li class="border-b border-outline-variant pb-2">
                <button 
                  @click="selectedArticleId = 'prompt-opt'; feedbackGiven = false;"
                  class="flex justify-between items-center group w-full text-left cursor-pointer"
                >
                  <span
                    class="font-label-md text-on-surface group-hover:text-primary"
                    >Prompt Optimization</span>
                  <span class="material-symbols-outlined text-outline text-[18px]">chevron_right</span>
                </button>
              </li>
              <li class="border-b border-outline-variant pb-2">
                <button 
                  @click="selectedArticleId = 'knowledge-retrieval'; feedbackGiven = false;"
                  class="flex justify-between items-center group w-full text-left cursor-pointer"
                >
                  <span
                    class="font-label-md text-on-surface group-hover:text-primary"
                    >Knowledge Retrieval</span>
                  <span class="material-symbols-outlined text-outline text-[18px]">chevron_right</span>
                </button>
              </li>
            </ul>
          </div>
          
          <!-- Account & Billing -->
          <div class="md:col-span-4 glass-card p-stack-lg rounded-xl">
            <span
              class="material-symbols-outlined text-primary mb-stack-md text-[32px]"
              >payments</span>
            <h3 class="font-headline-md text-headline-md text-deep-navy mb-2">
              Billing &amp; Account
            </h3>
            <p class="text-on-surface-variant font-body-md mb-stack-md">
              Manage subscriptions, update payment methods, and download
              historical invoices.
            </p>
            <button
              @click="selectedArticleId = 'manage-account'; feedbackGiven = false;"
              class="text-primary font-label-md flex items-center gap-1 group text-left cursor-pointer"
            >
              Manage Account
              <span
                class="material-symbols-outlined text-[18px] group-hover:translate-x-1 transition-transform"
                >arrow_forward</span>
            </button>
          </div>
          
          <!-- Security & Privacy -->
          <div class="md:col-span-4 glass-card p-stack-lg rounded-xl">
            <span
              class="material-symbols-outlined text-primary mb-stack-md text-[32px]"
              >security</span>
            <h3 class="font-headline-md text-headline-md text-deep-navy mb-2">
              Security
            </h3>
            <p class="text-on-surface-variant font-body-md mb-stack-md">
              Data residency, 2FA setup, audit logs, and compliance certifications
              (SOC2, HIPAA).
            </p>
            <button
              @click="selectedArticleId = 'trust-center'; feedbackGiven = false;"
              class="text-primary font-label-md flex items-center gap-1 group text-left cursor-pointer"
            >
              Trust Center
              <span
                class="material-symbols-outlined text-[18px] group-hover:translate-x-1 transition-transform"
                >arrow_forward</span>
            </button>
          </div>
          
          <!-- API & Developers -->
          <div class="md:col-span-4 glass-card p-stack-lg rounded-xl">
            <span
              class="material-symbols-outlined text-primary mb-stack-md text-[32px]"
              >code</span>
            <h3 class="font-headline-md text-headline-md text-deep-navy mb-2">
              Developers
            </h3>
            <p class="text-on-surface-variant font-body-md mb-stack-md">
              Full REST API documentation, Webhook events, and SDKs for React,
              Node, and Python.
            </p>
            <button
              @click="selectedArticleId = 'api-docs'; feedbackGiven = false;"
              class="text-primary font-label-md flex items-center gap-1 group text-left cursor-pointer"
            >
              API Docs
              <span
                class="material-symbols-outlined text-[18px] group-hover:translate-x-1 transition-transform"
                >arrow_forward</span>
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Community Teaser Section -->
    <section class="py-stack-xl bg-deep-navy text-on-primary">
      <div
        class="max-w-container-max mx-auto px-margin-desktop grid grid-cols-1 md:grid-cols-2 gap-stack-xl items-center"
      >
        <div>
          <h2 class="font-headline-lg text-headline-lg mb-stack-md">
            Join the Architect Community
          </h2>
          <p class="text-primary-fixed-dim font-body-lg mb-stack-lg">
            Connect with other OS DESK users to share best practices, custom
            agent prompts, and workflow automation templates.
          </p>
          <div class="flex flex-wrap gap-stack-md">
            <button
              class="bg-secondary text-on-secondary px-stack-lg py-stack-sm rounded-lg font-label-md hover:brightness-110 transition-all cursor-pointer"
            >
              Browse Forum
            </button>
            <button
              class="border border-outline-variant px-stack-lg py-stack-sm rounded-lg font-label-md hover:bg-white/10 transition-all cursor-pointer"
            >
              Discord Server
            </button>
          </div>
        </div>
        <div class="relative">
          <div
            class="bg-primary-container rounded-2xl p-stack-lg relative overflow-hidden border border-white/10"
          >
            <div class="flex items-center gap-4 mb-stack-md">
              <div class="w-10 h-10 rounded-full bg-secondary" />
              <div>
                <p class="font-label-md text-white">Latest Discussion</p>
                <p class="text-caption text-primary-fixed-dim">3 minutes ago</p>
              </div>
            </div>
            <h4 class="font-headline-md text-white mb-stack-sm">
              "How are you handling high-volume ticket bursts with GPT-4o?"
            </h4>
            <p class="text-outline-variant font-body-md line-clamp-2">
              Our team recently implemented a tiered routing system where the
              agent handles first-response then escalates based on sentiment
              analysis...
            </p>
            <div class="mt-stack-md flex items-center gap-stack-md">
              <div class="flex -space-x-3">
                <div
                  class="w-8 h-8 rounded-full border-2 border-primary-container bg-surface-container-highest"
                />
                <div
                  class="w-8 h-8 rounded-full border-2 border-primary-container bg-secondary-fixed"
                />
                <div
                  class="w-8 h-8 rounded-full border-2 border-primary-container bg-surface-dim"
                />
              </div>
              <span class="text-caption text-outline-variant">42 others are discussing this</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Contact Support Fallback -->
    <section class="py-24 max-w-4xl mx-auto px-margin-desktop text-center">
      <div
        class="bg-surface-container rounded-3xl p-stack-xl border border-outline-variant"
      >
        <h2
          class="font-headline-lg text-headline-lg text-deep-navy mb-stack-sm"
        >
          Still can't find what you're looking for?
        </h2>
        <p class="text-on-surface-variant font-body-lg mb-stack-lg">
          Our human support team is available 24/7 for Enterprise customers and
          during business hours for all other plans.
        </p>
        <div class="flex flex-col sm:flex-row justify-center gap-stack-md">
          <a
            href="mailto:support@onlinesavaari.com"
            class="flex-1 bg-surface-container-lowest p-stack-md rounded-xl border border-outline-variant hover:border-primary transition-colors cursor-pointer text-left block"
          >
            <span class="material-symbols-outlined text-primary mb-2">mail</span>
            <h5 class="font-label-md text-on-surface">Email Support</h5>
            <p class="text-caption text-on-surface-variant">
              support@onlinesavaari.com
            </p>
          </a>
          <a
            href="https://wa.me/917003601355"
            target="_blank"
            rel="noopener noreferrer"
            class="flex-1 bg-surface-container-lowest p-stack-md rounded-xl border border-outline-variant hover:border-primary transition-colors cursor-pointer text-left block"
          >
            <span class="material-symbols-outlined text-primary mb-2">chat</span>
            <h5 class="font-label-md text-on-surface">WhatsApp Support</h5>
            <p class="text-caption text-on-surface-variant">
              +91 7003601355
            </p>
          </a>
        </div>
      </div>
    </section>

    <!-- Footer (Shared Component) -->
    <Footer />
  </div>
</template>
